import { lazy, Suspense, useEffect } from "react";
import { useAuth } from "./auth/AuthContext";
import { LoadingState } from "./components/LoadingState";
import { navigate, usePathname } from "./lib/router";

const LandingPage = lazy(() => import("./pages/LandingPage").then(({ LandingPage }) => ({ default: LandingPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then(({ LoginPage }) => ({ default: LoginPage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then(({ SignupPage }) => ({ default: SignupPage })));
const PendingPage = lazy(() => import("./pages/PendingPage").then(({ PendingPage }) => ({ default: PendingPage })));
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp").then(({ AuthenticatedApp }) => ({ default: AuthenticatedApp })));

const pageTitles: Record<string, string> = {
  "/": "YieldBoost | Multifamily performance",
  "/login": "Sign in | YieldBoost",
  "/signup": "Request access | YieldBoost",
  "/pending": "Access request | YieldBoost",
  "/app": "Today | YieldBoost",
  "/today": "Today | YieldBoost",
  "/revenue": "Revenue | YieldBoost",
  "/leads": "Leads | YieldBoost",
  "/tours": "Tours | YieldBoost",
  "/insights": "Insights | YieldBoost",
  "/controls": "Controls | YieldBoost",
  "/overview": "Portfolio overview | YieldBoost",
  "/portfolio": "Portfolio | YieldBoost",
  "/recommendations": "Recommendations | YieldBoost",
  "/scenario": "Scenario lab | YieldBoost",
  "/audit": "Audit trail | YieldBoost",
  "/account": "Account | YieldBoost",
  "/admin/users": "User access | YieldBoost",
};

function SignInRedirect() {
  useEffect(() => navigate("/login"), []);
  return <LoadingState />;
}

function WorkspaceRedirect() {
  useEffect(() => navigate("/app"), []);
  return <LoadingState />;
}

export default function App() {
  const pathname = usePathname();
  const { user, loading } = useAuth();

  useEffect(() => {
    const detailTitle = pathname.startsWith("/properties/")
      ? "Property intelligence | YieldBoost"
      : pathname.startsWith("/recommendations/")
        ? "Recommendation review | YieldBoost"
        : undefined;
    document.title = pageTitles[pathname] ?? detailTitle ?? "YieldBoost";
  }, [pathname]);

  if (pathname === "/") {
    return <Suspense fallback={<LoadingState />}><LandingPage /></Suspense>;
  }

  if (["/login", "/signup"].includes(pathname)) {
    if (loading) return <LoadingState />;
    if (user) return <WorkspaceRedirect />;
    const publicPage = pathname === "/login" ? <LoginPage /> : <SignupPage />;
    return <Suspense fallback={<LoadingState />}>{publicPage}</Suspense>;
  }

  if (pathname === "/pending") {
    return <Suspense fallback={<LoadingState />}><PendingPage /></Suspense>;
  }

  if (loading) return <LoadingState />;
  if (!user) return <SignInRedirect />;
  return <Suspense fallback={<LoadingState />}><AuthenticatedApp pathname={pathname} user={user} /></Suspense>;
}
